import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

const GOLD = '#9d6f32';
const GOLD_DEEP = '#7a5218';
const DARK = '#1a1a1a';
const CREAM = '#faf9f7';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockedUntilMs, setLockedUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loginBgUrl, setLoginBgUrl] = useState<string | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'config', 'loginPage'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setLogoUrl(String(data?.logoUrl || '').trim() || null);
          setLoginBgUrl(String(data?.loginBgUrl || '').trim() || null);
        }
      })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, []);

  useEffect(() => {
    if (!lockedUntilMs) return undefined;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (now >= lockedUntilMs) setLockedUntilMs(null);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [lockedUntilMs]);

  const lockSecondsRemaining = lockedUntilMs
    ? Math.max(0, Math.ceil((lockedUntilMs - nowMs) / 1000))
    : 0;
  const isLocked = lockSecondsRemaining > 0;
  const lockMessage = isLocked
    ? `Too many unsuccessful attempts. Try again in ${Math.ceil(lockSecondsRemaining / 60)} minute${Math.ceil(lockSecondsRemaining / 60) === 1 ? '' : 's'}.`
    : '';

  const guardLoginAttempt = async (outcome: 'check' | 'failed' | 'success') => {
    const guard = httpsCallable<
      { email: string; outcome: 'check' | 'failed' | 'success' },
      { locked: boolean; lockedUntilMs: number | null }
    >(functions, 'guardLoginAttempt');
    const result = await guard({ email: email.trim().toLowerCase(), outcome });
    return result.data;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || isLocked) return;
    setError('');
    setLoading(true);
    try {
      const lockStatus = await guardLoginAttempt('check').catch(() => null);
      if (lockStatus?.locked && lockStatus.lockedUntilMs) {
        setLockedUntilMs(lockStatus.lockedUntilMs);
        return;
      }
      await signInWithEmailAndPassword(auth, email.trim(), password);
      void guardLoginAttempt('success').catch(() => undefined);
    } catch (loginError: any) {
      const code = String(loginError?.code || '');
      const isInvalidCredential = [
        'auth/invalid-credential',
        'auth/wrong-password',
        'auth/user-not-found',
      ].includes(code);
      if (isInvalidCredential) {
        const lockStatus = await guardLoginAttempt('failed').catch(() => null);
        if (lockStatus?.locked && lockStatus.lockedUntilMs) {
          setLockedUntilMs(lockStatus.lockedUntilMs);
          return;
        }
      }
      setError('Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Left panel ── */
  const leftPanel = (
    <Box
      sx={{
        position: 'relative',
        width: { md: '55%' },
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: DARK,
        overflow: 'hidden',
        minHeight: '100vh',
      }}
    >
      {loginBgUrl && (
        <Box
          component="img"
          src={loginBgUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: loginBgUrl
            ? 'linear-gradient(160deg, rgba(10,7,3,0.72) 0%, rgba(20,12,4,0.60) 100%)'
            : 'linear-gradient(160deg, #1a1a1a 0%, #111108 100%)',
        }}
      />

      {/* Gold corner accents */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 180, height: 180, background: `radial-gradient(circle at 0 0, ${GOLD}22 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 220, height: 220, background: `radial-gradient(circle at 100% 100%, ${GOLD}18 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          px: 5,
          textAlign: 'center',
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt="Swarna Group"
            sx={{ maxWidth: 160, maxHeight: 100, objectFit: 'contain', filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))' }}
          />
        ) : (
          <Typography sx={{ fontSize: 36, fontWeight: 800, color: GOLD, letterSpacing: 2, textTransform: 'uppercase', lineHeight: 1.1 }}>
            SWARNA<br />GROUP
          </Typography>
        )}
        <Box sx={{ width: 48, height: 2, bgcolor: GOLD, borderRadius: 1, opacity: 0.7 }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, letterSpacing: 2.5, textTransform: 'uppercase', fontWeight: 500 }}>
          Real Estate Management
        </Typography>
      </Box>

      <Typography
        sx={{
          position: 'absolute', bottom: 28, left: 0, right: 0,
          textAlign: 'center', color: 'rgba(255,255,255,0.25)',
          fontSize: 11.5, letterSpacing: 0.5, zIndex: 1,
        }}
      >
        © {new Date().getFullYear()} Swarna Group. All rights reserved.
      </Typography>
    </Box>
  );

  /* ── Right panel ── */
  const rightPanel = (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // vertically centered — no longer top-anchored
        justifyContent: 'center',
        bgcolor: CREAM,
        minHeight: '100vh',
        px: { xs: 2.5, sm: 5, md: 6 },
        py: { xs: 6, md: 4 },
        position: 'relative',
      }}
    >
      {/* Desktop branding anchor — top-left */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'absolute',
          top: 36,
          left: 48,
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt="Swarna"
            sx={{ height: 52, maxWidth: 160, objectFit: 'contain', opacity: 0.9 }}
          />
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: GOLD_DEEP, letterSpacing: 2.5, textTransform: 'uppercase', opacity: 0.75 }}>
            SWARNA GROUP
          </Typography>
        )}
      </Box>

      {/* Mobile-only logo */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', alignItems: 'center', mb: 4, gap: 1.5 }}>
        {logoUrl ? (
          <Box component="img" src={logoUrl} alt="Swarna Group" sx={{ maxWidth: 120, maxHeight: 72, objectFit: 'contain' }} />
        ) : (
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: GOLD, letterSpacing: 1.5, textTransform: 'uppercase' }}>
            SWARNA GROUP
          </Typography>
        )}
        <Box sx={{ width: 36, height: 2, bgcolor: GOLD, borderRadius: 1, opacity: 0.6 }} />
      </Box>

      {/* Form card */}
      <Box sx={{ width: '100%', maxWidth: 360 }}>
        <Typography
          sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: DARK, mb: 0.75, letterSpacing: -0.3 }}
        >
          Welcome back
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: '#7a7a7a', mb: 2 }}>
          Sign in to your account to continue
        </Typography>

        {/* Gold rule separator */}
        <Box sx={{ width: 32, height: 1.5, bgcolor: GOLD, borderRadius: 1, mb: 3, opacity: 0.65 }} />

        <Box component="form" onSubmit={handleLogin} noValidate>
          <TextField
            fullWidth
            label="Email address"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              mb: 2.25,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#fff',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.32)' },
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#fff',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.18)' },
                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.32)' },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword
                      ? <VisibilityOffOutlinedIcon fontSize="small" sx={{ color: '#aaa' }} />
                      : <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#aaa' }} />
                    }
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Forgot password */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Typography
              component="button"
              type="button"
              sx={{
                fontSize: 13, color: GOLD, background: 'none', border: 'none',
                cursor: 'pointer', p: 0, fontFamily: 'inherit',
                '&:hover': { color: GOLD_DEEP, textDecoration: 'underline' },
              }}
            >
              Forgot password?
            </Typography>
          </Box>

          {(error || isLocked) && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px', fontSize: 13.5 }}>
              {isLocked ? lockMessage : error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            disabled={loading || isLocked || !email.trim() || !password}
            sx={{
              height: 50,
              borderRadius: '10px',
              bgcolor: GOLD_DEEP,
              color: '#fff',
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: 0.3,
              textTransform: 'none',
              boxShadow: '0 4px 20px rgba(122,82,24,0.30)',
              '&:hover': { bgcolor: GOLD, boxShadow: '0 6px 28px rgba(122,82,24,0.40)' },
              '&:disabled': { bgcolor: '#d4c4a8', color: '#fff', boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : isLocked ? 'Temporarily locked' : 'Sign In'}
          </Button>
        </Box>
      </Box>

      {/* Desktop trust footer */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'absolute',
          bottom: 28,
          left: 0,
          right: 0,
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Lock icon inline SVG */}
        <Box
          component="svg"
          viewBox="0 0 16 16"
          sx={{ width: 12, height: 12, fill: 'none', stroke: '#bbb', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', flexShrink: 0 }}
        >
          <rect x="3" y="7" width="10" height="8" rx="1.5" />
          <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#bbb', letterSpacing: 0.4 }}>
          Secure access · Swarna Group CRM
        </Typography>
      </Box>

      {/* Mobile copyright */}
      <Typography
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          bottom: 20,
          fontSize: 11.5,
          color: '#bbb',
          textAlign: 'center',
        }}
      >
        © {new Date().getFullYear()} Swarna Group
      </Typography>
    </Box>
  );

  if (!configLoaded) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: CREAM }}>
        <CircularProgress size={28} sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {leftPanel}
      {rightPanel}
    </Box>
  );
}
